import React, { useMemo } from 'react';
import { ITheme, ThemeProvider as StyledComponentsThemeProvider, createGlobalStyle, css } from 'styled-components';
import { useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { Colors } from './Theme.styled';
import { darkModeSelector } from './Theme.selector';
import { FONTS } from './Theme.fonts';

const MEDIA_WIDTHS = {
    upToExtraSmall: 500,
    upToSmall: 720,
    upToMedium: 960,
    upToLarge: 1280,
};

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce(
    (accumulator, size) => {
        (accumulator as any)[size] = (a: any, b: any, c: any) => css`
            @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
                ${css(a, b, c)}
            }
        `;
        return accumulator;
    },
    {},
) as any;

const white = '#FFFFFF';
const black = '#0A0909';

export function colors(darkMode: boolean): Colors {
    return {
        // base
        white,
        black,

        // table
        headerRow: darkMode ? '#f5f5f5' : '#f5f5f5',
        hoverRow: darkMode ? '#e0e0e0' : '#e0e0e0',
        darkRow: darkMode ? '#F5F5F5' : '#F5F5F5',
        lightRow: darkMode ? white : white,

        // modal
        modalBg: darkMode ? 'rgba(0,0,0,.425)' : 'rgba(0,0,0,0.3)',
        background: darkMode ? black : white,
        background1: darkMode ? '#1B1B1F' : '#1B1B1F',

        border1: darkMode ? '#57FF67' : '#0A0909',
        border2: darkMode ? '#C5C8CC' : '#0A0909',
        border3: darkMode ? '#6A6C7B' : '#6A6C7B',
        border4: darkMode ? '#0A0909' : '#57FF67',
        border5: darkMode ? 'rgba(87, 255, 103, 0.3)' : 'rgba(87, 255, 103, 0.3)',
        border6: darkMode ? '#373843' : '#6A6C7B',

        // text
        text: darkMode ? black : white,
        text1: darkMode ? white : black,
        text2: darkMode ? '#c5c8cc' : black,
        text3: darkMode ? '#FFF' : '#FFF',
        text4: darkMode ? '#9C9EA5' : '#6A6C7B',
        text5: darkMode ? '#F5F5F5' : '#F5F5F5',
        text6: darkMode ? '#6A6C7B' : '#6A6C7B',
        text7: darkMode ? '#F5F5F5' : '#6A6C7B',
        text8: darkMode ? white : '#6A6C7B',
        text9: darkMode ? white : '#6A6C7B',

        green1: darkMode ? '#6B83FF' : '#6B83FF',
        green2: darkMode ? '#90EE99' : '#90EE99',
        green3: darkMode ? '#55CF68' : '#55CF68',
        green4: darkMode ? 'rgba(87, 255, 103, 0.3)' : 'rgba(255, 255, 255, 0.5)',
        green5: darkMode ? '#57FF67' : '#949494',
        green6: darkMode ? 'rgba(87, 255, 103, 0.7)' : 'rgba(87, 255, 103, 0.7)',
        red1: darkMode ? '#EF466F' : '#EF466F',

        yellow1: darkMode ? '#FFD166' : '#FFD166',
        yellow2: darkMode ? '#FF8A00' : '#FF8A00',

        tooltipBg: black,
        tooltipText: white,

        gray1: darkMode ? '#D8D8D8' : '#D8D8D8',
        gray2: darkMode ? '#D6D8E0' : '#D6D8E0',
        gray3: darkMode ? '#D1D1D1' : '#D1D1D1',

        dark1: darkMode ? '#C5C8CC' : '#C5C8CC',
        dark2: darkMode ? '#0A0909' : '#0A0909',

        dark3: darkMode ? '#777E90' : '#777E90',
        dark4: darkMode ? '#B1B5C3' : '#B1B5C3',
        dark5: darkMode ? '#232323' : '#232323',
        dark6: darkMode ? '#777E91' : '#777E91',
        dark7: darkMode ? '#23262F' : '#23262F',
        dark8: darkMode ? '#141416' : '#141416',
        dark9: darkMode ? '#202231' : '#202231',
        dark10: darkMode ? '#1B1B1F' : '#1B1B1F',

        blue1: darkMode ? '#3772ff' : '#3772ff',
        blue2: darkMode ? '#8084dc' : '#8084dc',
        blue3: darkMode ? '#4BC9F0' : '#4BC9F0',
        blue4: darkMode ? '#15B2F4' : '#15B2F4',
    };
}

export function appTheme(darkMode: boolean): ITheme {
    return {
        ...colors(darkMode),

        grids: {
            sm: 8,
            md: 12,
            lg: 24,
        },

        // media queries
        mediaWidth: mediaWidthTemplates,

        // css snippets
        flexColumnNoWrap: css`
            display: flex;
            flex-flow: column nowrap;
        `,
        flexRowNoWrap: css`
            display: flex;
            flex-flow: row nowrap;
        `,
    };
}

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const darkMode = useSelector(darkModeSelector);

    const themeObject = useMemo(() => appTheme(darkMode), [darkMode]);

    return <StyledComponentsThemeProvider theme={themeObject}>{children}</StyledComponentsThemeProvider>;
};

export const FixedGlobalStyle = createGlobalStyle`
    html,
    body {
        margin: 0;
        padding: 0;
    }
    ::-webkit-scrollbar {
        width: 7px;
        background: #000;
    }
    
    ::-webkit-scrollbar-thumb {
        background: #6A6C7B;
        -webkit-border-radius: 1ex;
        -webkit-box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.75);
    }
    .ant-modal-close-x {
        display: none;
    }

    .ant-modal-body {
        padding: 0;
    }

    .ant-modal-content {
        background-color: transparent;
        box-shadow: 0px 0px 10px  #6b84ffb3;
    }

    a {
        color: ${colors(false).black};
    }

    .modified-item:hover {
        border-bottom: 2px solid transparent !important;
        color: inherit !important;
    }

    * {
        box-sizing: border-box;
    }

    button {
        user-select: none;
    }

    .container {
        max-width: 1272px;
        margin: 0 auto;
    }

    html {
        font-size: 16px;
        font-variant: none;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        font-feature-settings: 'ss01' on, 'ss02' on, 'cv01' on, 'cv03' on;
    }
    p {
        margin-bottom: 0;
    }

    .ant-picker-dropdown {
        .ant-picker-panel-container {
            background: #111111 !important;
        }

        .ant-picker-header-view {
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
        }

        .ant-picker-header {
            border-bottom: 1px solid #000;
            color: #ffffff;
            fill: #ffffff;

        }

        .ant-picker-time-panel {
            border-left: 1px solid #000;
        }

        .ant-picker-panel .ant-picker-footer {
            border-top: 1px solid #000;
        }
        
        .ant-picker-footer {
            border-bottom: 1px solid #000;
        }

        .ant-picker-content thead tr th {
            color: #ffffff;
        }

        .ant-picker-content tbody tr td.ant-picker-cell {
            color: #302E2E;
        }

        .ant-picker-content tbody tr td.ant-picker-cell.ant-picker-cell-disabled {
            color: #302E2E;
        }

        .ant-picker-content tbody tr td.ant-picker-cell-disabled {
            color: #706E6E;
        }

        .ant-picker-content tbody tr td.ant-picker-cell-disabled.ant-picker-cell-in-view {
            color: #706E6E;
        }

        .ant-picker-content tbody tr td.ant-picker-cell-in-view {
            color: #ffffff;
        }

        .ant-picker-content tbody tr td.ant-picker-cell-in-view:hover {
            color: #000000;
        }

        .ant-picker-content .ant-picker-time-panel-column .ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner {
            color: #ffffff;
            display: flex;
            justify-content: center;
            align-items: center;
            padding-left: 0;
        }

        .ant-picker-content .ant-picker-time-panel-column .ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner:hover {
            color: #000000;
            background: #A8A7A7;
        }

        .ant-picker-content .ant-picker-time-panel-column .ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner {
            color: #1A1A1A;
        }

        .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner {
            background: #A8A7A7;
        }

        // .ant-picker-time-panel-column:not(:first-child) {
        //     display: none;
        // }

        .ant-picker-now-btn {
            color: #ffffff;
        }

        .ant-btn-primary[disabled] {
            color: #000;
        }

        .ant-picker-panel {
            border: none;
        }

        .ant-picker-header button {
            color: #ffffff;
        }
    }
`;

export const ThemedGlobalStyle = createGlobalStyle`
    body {
        min-height: 100vh;
        box-sizing: border-box;
        background-color: ${({ theme }: { theme: ITheme }) => theme.black};
    }

    * {
        box-sizing: border-box;
        // font-family: 'Orbitron', sans-serif;
        font-family: 'Montserrat', sans-serif;
    }

    .btn-primary {
        width: 100%;
        transition: .2s ease-in-out 0s;
        height: 40px;
        color: ${({ theme }: { theme: ITheme }) => theme.text};
        font-weight: normal;
        background: ${({ theme }: { theme: ITheme }) => theme.green1};
        // border: 1px solid ${({ theme }: { theme: ITheme }) => theme.border1};
        border-radius: 4px;
    }

    .btn-primary:hover {
        cursor: pointer;
        transform: scale(1.03);
    }

    .btn-primary:active {
        cursor: pointer;
        opacity: 0.8;
        color: ${({ theme }: { theme: ITheme }) => theme.dark1};
    }
    // .ant-btn:hover {
    //     background: ${({ theme }: { theme: ITheme }) => theme.green1} !important;
    //     border: 1px solid ${({ theme }: { theme: ITheme }) => theme.border1} !important;
    //     color: ${({ theme }: { theme: ITheme }) => theme.text} !important;
    // }

    ${({ theme }: { theme: ITheme }) => theme.mediaWidth.upToMedium`
        .btn-primary {
            height: 40px;
        }
    `}
    
    .fw-regular {
        font-weight: 400;
    }

    .fw-medium {
        font-weight: 500;
    }

    .fw-light {
        font-weight: 200;
    }

    .fw-suppermedium,
    .fw-supermedium {
        font-weight: 600;
    }

    .fw-bold {
        font-weight: 700;
    }

    .fs-suppersmal,
    .fs-suppersmall {
        font-size: ${FONTS.SIZE.superSmall}px;
        line-height: ${FONTS.SIZE.superSmall + 7}px;
    }

    .fs-small {
        font-size: ${FONTS.SIZE.small - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.small + 7 - (isMobile ? 2 : 0)}px;
    }

    .fs-regular {
        font-size: ${FONTS.SIZE.regular - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.regular + 7 - (isMobile ? 2 : 0)}px;
    }

    .fs-medium {
        font-size: ${FONTS.SIZE.medium - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.medium + 7 - (isMobile ? 2 : 0)}px;
    }

    .fs-mediumlarge {
        font-size: ${FONTS.SIZE.mediumLarge - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.mediumLarge + 7 - (isMobile ? 2 : 0)}px;
    }

    .fs-supermedium {
        font-size: ${FONTS.SIZE.superMedium - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.superMedium + 7 - (isMobile ? 2 : 0)}px;
    }

    .fs-large {
        font-size: ${FONTS.SIZE.large - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.large + 7 - (isMobile ? 2 : 0)}px;
    }

    .fs-avglarge {
        font-size: ${FONTS.SIZE.avgLarge - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.avgLarge + 7 - (isMobile ? 2 : 0)}px;
    }

    .fs-verylarge {
        font-size: ${FONTS.SIZE.veryLarge - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.veryLarge + 7 - (isMobile ? 2 : 0)}px;
    }

    .fs-superlarge {
        font-size: ${FONTS.SIZE.superLarge - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.superLarge + 7 - (isMobile ? 2 : 0)}px;
    }
    .fs-titlemedium {
        font-size: ${FONTS.SIZE.titleMedium - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.titleMedium + 7 - (isMobile ? 2 : 0)}px;
    }

    .fs-36 {
        font-size: ${FONTS.SIZE.fs36 - (isMobile ? 2 : 0)}px;
        line-height: ${FONTS.SIZE.fs36 + 7 - (isMobile ? 2 : 0)}px;
    }
    
    .center {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .row {
        display: flex;
        flex-direction: row;
    }
    .disable-pointer {
        cursor: default;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }
    .gradient-text {
        background: -webkit-linear-gradient(-75deg, #15b2f4 0%, #9546c2 50%, #fd7942 75%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        &::selection {
            -webkit-text-fill-color: white;
        }
    }
    .items-center {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .justify-between {
        display: flex;
        justify-content: space-between;
    }
    .btn-border {
        color: ${({ theme }: { theme: ITheme }) => theme.text3};
        border: 1px solid ${({ theme }: { theme: ITheme }) => theme.border1};
        font-size: 12px !important;
        background: transparent !important;
    }
    .text-center {
        text-align: center;
    }
    .default-text {
        color: ${({ theme }: { theme: ITheme }) => theme.text1};
    }
    .model-solana-custom {
        .ant-menu-submenu-title:hover {
            .ant-menu-title-content,
            .ant-menu-submenu-arrow {
                color: ${({ theme }: { theme: ITheme }) => theme.black} !important;
            }
        }
    }
    .drawer-header-mobile {
        .ant-drawer-body {
            padding: 0 !important;
        }
    }
    .option-custom {
        display: flex !important;
        align-items: center !important;
        padding: 2px 8px;
        background: none;
        border-radius: 4px;
        span {
            margin-left: 12px;
            color: #b1b5c4;
        }
        .ant-image {
            width: 25px;
        }
    }
    .ant-select-dropdown {
        background: #353945;
        box-shadow: -2px 4px 16px rgba(0, 0, 0, 0.7);
        border-radius: 4px;
        width: 200px;
      }
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
        background: #353945;
        .option-custom {
            color: white;
            background: #23262F;
        }
    }
    .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
        background: #23262F;
        span {
            color: white;
        }
    }
    .green-text {
        color: ${({ theme }: { theme: ITheme }) => theme.green1};
    }
    .ant-drawer-close {
        position: absolute;
    }
    .ant-select-item-option-content {
        color: white;
    }
`;
